<template>
  <div class="transaction-breakdown-row p-3 bg-light">
    <LoadingIndicatorComponent v-if="isLoading" />

    <div v-else-if="hasError" class="alert alert-warning mb-0">
      Failed to load breakdown data.
    </div>

    <div v-else class="row g-3 mb-3">
      <!-- Production Months -->
      <div class="col-md-4 col-lg-2">
        <div class="card h-100">
          <div class="card-header bg-white py-1">
            <small class="fw-bold">Production Months</small>
          </div>
          <div class="card-body p-0">
            <table class="table table-sm mb-0">
              <tbody>
                <tr v-for="[month, mwh] in sortedProductionMonths" :key="month">
                  <td class="px-2 py-1">{{ formatProductionMonth(month) }}</td>
                  <td class="text-end px-2 py-1">{{ mwh }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Regions -->
      <div v-if="hasRegions" class="col-md-4 col-lg-2">
        <div class="card h-100">
          <div class="card-header bg-white py-1">
            <small class="fw-bold">Regions</small>
          </div>
          <div class="card-body p-0">
            <table class="table table-sm mb-0">
              <tbody>
                <tr
                  v-for="(breakdown, region) in regionBreakdown"
                  :key="region"
                >
                  <td class="px-2 py-1">{{ region }}</td>
                  <td class="text-end px-2 py-1">{{ breakdown.total_mwh }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Technology -->
      <div v-if="technologyBreakdown.length > 0" class="col-md-4 col-lg-2">
        <div class="card h-100">
          <div class="card-header bg-white py-1">
            <small class="fw-bold">Technology</small>
          </div>
          <div class="card-body p-0">
            <table class="table table-sm mb-0">
              <tbody>
                <tr v-for="entry in technologyBreakdown" :key="entry.label">
                  <td class="px-2 py-1">{{ entry.label }}</td>
                  <td class="text-end px-2 py-1">{{ entry.mwh }}</td>
                  <td class="text-end px-2 py-1 text-muted">
                    {{ entry.percentage }}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Support Status -->
      <div v-if="supportBreakdown.length > 0" class="col-md-4 col-lg-2">
        <div class="card h-100">
          <div class="card-header bg-white py-1">
            <small class="fw-bold">Support Status</small>
          </div>
          <div class="card-body p-0">
            <table class="table table-sm mb-0">
              <tbody>
                <tr v-for="entry in supportBreakdown" :key="entry.label">
                  <td class="px-2 py-1">{{ entry.label }}</td>
                  <td class="text-end px-2 py-1">{{ entry.mwh }}</td>
                  <td class="text-end px-2 py-1 text-muted">
                    {{ entry.percentage }}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- COD / RE100 / Ecolabel -->
      <div class="col-md-4 col-lg-2">
        <div class="card h-100">
          <div class="card-header bg-white py-1">
            <small class="fw-bold">COD Age</small>
          </div>
          <div class="card-body p-0">
            <table class="table table-sm mb-0">
              <tbody>
                <tr v-for="entry in codBreakdown" :key="entry.label">
                  <td class="px-2 py-1">{{ entry.label }}</td>
                  <td class="text-end px-2 py-1">{{ entry.mwh }}</td>
                  <td class="text-end px-2 py-1 text-muted">
                    {{ entry.percentage }}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Ecolabels -->
      <div class="col-md-4 col-lg-2">
        <div class="card h-100">
          <div class="card-header bg-white py-1">
            <small class="fw-bold">Ecolabels & RE100</small>
          </div>
          <div class="card-body p-0">
            <table class="table table-sm mb-0">
              <tbody>
                <tr v-for="entry in ecolabelBreakdown" :key="entry.label">
                  <td class="px-2 py-1">{{ entry.label }}</td>
                  <td class="text-end px-2 py-1">{{ entry.mwh }}</td>
                  <td class="text-end px-2 py-1 text-muted">
                    {{ entry.percentage }}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="internalNotes"
      class="card mb-3"
      data-testid="transaction-internal-notes-panel"
    >
      <div class="card-header bg-white py-1">
        <small class="fw-bold">{{ internalNotesTitle }}</small>
      </div>
      <!-- Kept on one line: pre-wrap would otherwise render the template's indentation -->
      <!-- prettier-ignore -->
      <div class="card-body px-2 py-1 internal-notes">{{ internalNotes }}</div>
    </div>

    <BuyerInvoiceHistoryPanel :transaction="transaction" />
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { formatMonthYear } from "@/composables/formatUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import BuyerInvoiceHistoryPanel from "@/money/admin/transactions/components/BuyerInvoiceHistoryPanel.vue";
import { FlattenedPortfolioItem, SalesTransaction } from "@/types";
import { RE100_TECHNOLOGIES } from "@/types/technology";
import { capitalize } from "lodash-es";
import { computed, onMounted, ref } from "vue";

const { globalErrorHandler } = useGlobalHandlers();

interface BreakdownEntry {
  label: string;
  mwh: number;
  percentage: string;
}

const props = defineProps<{
  transaction: SalesTransaction;
}>();

// A delivery's staff notes live on its forward_delivery row (set on the deal page), a spot sale's on the transaction.
const internalNotes = computed(() =>
  props.transaction.forward_delivery
    ? props.transaction.forward_delivery.delivery_internal_notes
    : props.transaction.internal_notes
);
const internalNotesTitle = computed(() =>
  props.transaction.forward_delivery ? "Delivery notes" : "Internal notes"
);

const isLoading = ref(true);
const hasError = ref(false);
const portfolioItems = ref<
  (FlattenedPortfolioItem & { selected_count: number })[]
>([]);
const regionBreakdown = ref<Record<string, { total_mwh: number }>>({});

const totalMwh = computed(() =>
  portfolioItems.value.reduce((sum, item) => sum + item.selected_count, 0)
);

const hasRegions = computed(
  () => Object.keys(regionBreakdown.value).length > 0
);

const UNKNOWN_MONTH = "Unknown";

const sortedProductionMonths = computed(() => {
  const breakdown: Record<string, number> = {};
  for (const item of portfolioItems.value) {
    const month = item.production_month ?? UNKNOWN_MONTH;
    breakdown[month] = (breakdown[month] ?? 0) + item.selected_count;
  }
  return Object.entries(breakdown).sort(([a], [b]) => a.localeCompare(b));
});

const formatProductionMonth = (month: string): string =>
  /^\d{4}-\d{2}$/.test(month) ? formatMonthYear(month) : month;

const technologyBreakdown = computed((): BreakdownEntry[] => {
  const groups: Record<string, number> = {};
  for (const item of portfolioItems.value) {
    const label = item.technology_type
      ? capitalize(item.technology_type)
      : "Unknown";
    groups[label] = (groups[label] ?? 0) + item.selected_count;
  }
  const total = totalMwh.value;
  return Object.entries(groups)
    .sort(([, a], [, b]) => b - a)
    .map(([label, mwh]) => ({
      label,
      mwh,
      percentage: total > 0 ? ((mwh / total) * 100).toFixed(1) : "0.0",
    }));
});

const supportBreakdown = computed((): BreakdownEntry[] => {
  const groups: Record<string, number> = {};
  for (const item of portfolioItems.value) {
    // null = registry gave no support data (e.g. CNMC imports) — don't claim "Unsupported"
    const label =
      item.production_support === true
        ? "Supported"
        : item.production_support === false
          ? "Unsupported"
          : "Unknown";
    groups[label] = (groups[label] ?? 0) + item.selected_count;
  }
  const total = totalMwh.value;
  return Object.entries(groups)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, mwh]) => ({
      label,
      mwh,
      percentage: total > 0 ? ((mwh / total) * 100).toFixed(1) : "0.0",
    }));
});

const codBreakdown = computed((): BreakdownEntry[] => {
  const groups: Record<string, number> = {};
  for (const item of portfolioItems.value) {
    let label: string;
    if (item.cod_age == null) {
      label = "Unknown";
    } else if (item.cod_age <= 5) {
      label = "≤5 years";
    } else if (item.cod_age <= 15) {
      label = "6-15 years";
    } else {
      label = ">15 years";
    }
    groups[label] = (groups[label] ?? 0) + item.selected_count;
  }
  const total = totalMwh.value;
  const order = ["≤5 years", "6-15 years", ">15 years", "Unknown"];
  return order
    .filter((label) => groups[label])
    .map((label) => ({
      label,
      mwh: groups[label],
      percentage:
        total > 0 ? ((groups[label] / total) * 100).toFixed(1) : "0.0",
    }));
});

const ecolabelBreakdown = computed((): BreakdownEntry[] => {
  const total = totalMwh.value;
  let ekoEnergyMwh = 0;
  let okPowerMwh = 0;
  let re100Mwh = 0;

  for (const item of portfolioItems.value) {
    const mwh = item.selected_count;
    if (item.ecolabel_approved === true) {
      ekoEnergyMwh += mwh;
    }
    if (item.okpower_new_compatible === true) {
      okPowerMwh += mwh;
    }
    if (
      item.technology_type &&
      RE100_TECHNOLOGIES.includes(item.technology_type.toLowerCase()) &&
      item.cod_age != null &&
      item.cod_age <= 15
    ) {
      re100Mwh += mwh;
    }
  }

  return [
    {
      label: "RE100",
      mwh: re100Mwh,
      percentage: total > 0 ? ((re100Mwh / total) * 100).toFixed(1) : "0.0",
    },
    {
      label: "EKOenergy",
      mwh: ekoEnergyMwh,
      percentage: total > 0 ? ((ekoEnergyMwh / total) * 100).toFixed(1) : "0.0",
    },
    {
      label: "ok-power new",
      mwh: okPowerMwh,
      percentage: total > 0 ? ((okPowerMwh / total) * 100).toFixed(1) : "0.0",
    },
  ];
});

const fetchTransactionDetails = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const response = await goSolidApi.get(
      `/api/admin/transactions/${props.transaction.id}`
    );
    if (response.data) {
      portfolioItems.value = response.data.portfolio_items ?? [];
      regionBreakdown.value = response.data.issuing_registry_breakdown ?? {};
    } else {
      hasError.value = true;
    }
  } catch (error) {
    globalErrorHandler(error);
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchTransactionDetails);
</script>

<style scoped lang="scss">
.transaction-breakdown-row {
  border-top: 1px solid #dee2e6;
}

.internal-notes {
  white-space: pre-wrap;
}
</style>
